import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Container, Heading, Screen, Stack, Text } from '@fluent/ui';

/**
 * The last line of defence. Without it, one thrown render error anywhere in
 * the tree unmounts the whole app and the learner is left staring at a white
 * screen with no way back — found in the 2026-09-22 audit (F144).
 *
 * Sentry's boundary is used rather than a hand-written one so the error is
 * reported with the React component stack attached. When Sentry has no DSN
 * (dev, and production until the DSN is pasted in) it still renders the
 * fallback — the boundary works on its own, reporting is the extra.
 */

function Fallback({ resetError }: { resetError: () => void }) {
  const { t } = useTranslation();
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={4} style={{ paddingTop: 64, textAlign: 'center' }}>
          <div aria-hidden="true" style={{ fontSize: 64, lineHeight: 1 }}>
            🦎
          </div>
          <Heading level={1}>{t('error.boundary_title')}</Heading>
          <Text tone="muted">{t('error.boundary_body')}</Text>
          <Stack gap={2} style={{ marginTop: 8 }}>
            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => {
                // Clear the error first: if the crash was transient (a bad
                // cache entry, a failed lazy chunk), this re-renders without
                // a full reload and keeps the user's session state.
                resetError();
                window.location.reload();
              }}
            >
              {t('error.boundary_reload')}
            </Button>
            <Button
              variant="ghost"
              size="md"
              fullWidth
              onClick={() => {
                resetError();
                window.location.assign('/');
              }}
            >
              {t('error.boundary_home')}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Screen>
  );
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <Fallback resetError={resetError} />}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
